import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import instance from 'api/apiClient';
import { apiRoutes } from 'configs/apiRoutes';
import {
  browserSessionStorage,
  readStoredParsingRun,
  resolveParsingProgress,
  type GameParsingRun,
} from './progress';

export type {
  GameParsingOutcome,
  GameParsingProviderResult,
  GameParsingProviderStatus,
  GameParsingRun,
  GameParsingStatus,
} from './progress';

export {
  normalizeGameParsingRun,
  parsingStatusDefaultLabel,
  parsingStatusLabelKey,
  readStoredParsingRun,
  resolveParsingProgress,
  writeStoredParsingRun,
} from './progress';

export type GameParsingState = {
  run: GameParsingRun | null;
  isLaunching: boolean;
};

export const launchFullGameParsing = createAsyncThunk(
  'gameParsing/launch',
  async (_, { rejectWithValue }) => {
    try {
      const result = await instance.post(apiRoutes.gameParseGames);
      const run = resolveParsingProgress(null, (result as { data?: unknown })?.data ?? result);
      if (!run) {
        return rejectWithValue(result);
      }
      return run;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState: GameParsingState = {
  run: readStoredParsingRun(browserSessionStorage()),
  isLaunching: false,
};

export const gameParsingSlice = createSlice({
  name: 'gameParsing',
  initialState,
  reducers: {
    applyParsingProgress: (state, action: PayloadAction<unknown>) => {
      state.run = resolveParsingProgress(state.run, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(launchFullGameParsing.pending, (state) => {
      state.isLaunching = true;
    });
    builder.addCase(launchFullGameParsing.fulfilled, (state, action) => {
      state.isLaunching = false;
      if (action.payload) {
        state.run = resolveParsingProgress(state.run, action.payload);
      }
    });
    builder.addCase(launchFullGameParsing.rejected, (state) => {
      state.isLaunching = false;
    });
  },
});

export const { applyParsingProgress } = gameParsingSlice.actions;
